/* eslint-disable */
import { FormInputState, FormModel, group, Result, ResultValue } from 'src_common/common/mobx-utils/Form';
import {
    PromotionQuestionsState,
    CsvReadFile,
    CsvReadItem,
} from '../../../AppState/PromotionQuestionsState/PromotionQuestionsState';
import { action, computed, observable, makeAutoObservable } from 'mobx';
import React from 'react';
import { selectFile } from '../../../utils/selectFile';
import * as Papa from 'papaparse';
import { validateLength, validateRequire, validateRequireFile } from 'src/api/utils/formValidators';
import { InputStatus } from 'src/components/Input/InputStatus';

interface FormToSendType {
    question: string;
    file: boolean;
    status: boolean;
}

interface FormType {
    question: string;
    file: boolean;
    status: boolean;
}

const _7_MB = 7340032;

export class QuestionFormState {
    readonly maxQuestionLength: number = 90;
    readonly maxFileSize: number = _7_MB;
    readonly allowedMimeTypes: string[] = ['text/csv'];
    readonly allowedFileTypes: string[] = ['.csv'];

    @observable isLoading: boolean = false;
    @observable errorMessage: string = '';

    @observable questionFileName: string | null = null;
    @observable questionFile: CsvReadFile | null = null;

    readonly questionData: CsvReadItem | null;

    readonly question: FormInputState<string>;
    readonly questionModel: FormModel<string>;
    readonly questionStatus: InputStatus;

    readonly file: FormInputState<boolean>;
    readonly fileModel: FormModel<boolean>;
    readonly fileStatus: InputStatus;

    readonly status: FormInputState<boolean>;
    readonly statusModel: FormModel<boolean>;

    readonly form: FormModel<FormToSendType>;

    constructor(private readonly promotionQuestionsState: PromotionQuestionsState) {
        this.promotionQuestionsState = promotionQuestionsState;

        this.questionData = promotionQuestionsState.currentQuestion;
        this.question = new FormInputState(this.questionData?.name || '', false);
        this.questionModel = this.question.map(validateRequire).map(validateLength(this.maxQuestionLength));
        this.questionStatus = new InputStatus(this.questionModel);

        if (this.questionData) {
            const { fileName, uploadedAt, possibleAnswers } = this.questionData;
            if (fileName && uploadedAt && possibleAnswers) {
                const file = new CsvReadFile(fileName, uploadedAt, possibleAnswers);
                this.questionFile = file;
            } else {
                this.questionFile = null;
            }
        } else {
            this.questionFile = null;
        }

        this.file = new FormInputState(Boolean(this.questionFile), false);
        this.fileModel = this.file.map(validateRequireFile);
        this.fileStatus = new InputStatus(this.fileModel);

        this.status = new FormInputState(this.questionData?.active || false, false);
        this.statusModel = this.status.toModel();

        this.form = group({
            question: this.questionModel,
            file: this.fileModel,
            status: this.statusModel,
        }).map((item: FormType): Result<FormToSendType> => {
            return new ResultValue({
                question: item.question,
                status: item.status,
                file: item.file,
            });
        });
        makeAutoObservable(this);
    }

    @computed get isFormValid(): boolean {
        return !this.questionFileName && this.form.result instanceof ResultValue;
    }

    @action handleSelectFile = async (files: File[]): Promise<void> => {
        const file = files[0];
        //@ts-expect-error
        this.questionFileName = file.name;
        try {
            //@ts-expect-error
            const parsedFile = await this.parseCSV(file);
            if (parsedFile.length > 0) {
                this.file.setValue(true);
                //@ts-expect-error
                this.questionFile = new CsvReadFile(file.name, Date.now().toString(), parsedFile.flat());
            } else {
                throw new Error('File is corrupted, unreadable or empty.');
            }
        } catch (error) {
            console.log(error);
        }
        this.questionFileName = null;
    };

    private parseCSV = async (file: File): Promise<string[]> => {
        return new Promise((resolve, reject) => {
            Papa.parse<string>(file, {
                error: (error) => {
                    reject(error.message);
                },
                complete: (result) => {
                    resolve(result.data);
                },
            });
        });
    };

    @action handleRemoveFile = (): void => {
        this.file.setValue(false);
        this.questionFileName = null;
        this.questionFile = null;
    };

    @action handleReplaceFile = async (event: React.SyntheticEvent): Promise<void> => {
        this.preventDefaults(event);
        const filesList = await selectFile({ acceptFileType: this.allowedFileTypes.join(',') });
        const files = Array.from(filesList);
        if (files.length > 0) {
            this.handleRemoveFile();
            await this.handleSelectFile(files);
        }
    };

    @action handleCancel = () => {
        this.promotionQuestionsState.setCurrentQuestion(null);
        this.form.reset();
    };

    @action handleSubmit = async () => {
        this.isLoading = true;
        this.errorMessage = '';
        this.form.setAsVisited();
        const result = this.form.result;

        if (result instanceof ResultValue) {
            const payload: Partial<CsvReadItem> = {
                id: this.questionData?.id,
                name: result.value.question,
                active: result.value.status,
                ...this.questionFile, // fileName, uploadedAt, possibleAnswers
            };

            const submitResult = await this.promotionQuestionsState.submitQuestion(payload);

            if (submitResult) {
                this.promotionQuestionsState.setCurrentQuestion(null);
                this.promotionQuestionsState.setRecentlyUpdatedQuestion(submitResult);
                await this.promotionQuestionsState.refreshQuestions();
                this.form.reset();
            } else {
                this.errorMessage = 'Unknown error. Something went wrong.';
            }
        }
        this.isLoading = false;
    };

    private preventDefaults = (event: React.SyntheticEvent) => {
        event.preventDefault();
        event.stopPropagation();
    };
}
